<template>
  <div>
    <div v-if="!disabled" class="d-flex flex-row justify-content-between">
      <div class="flex-fill pr-4">
        <custom-input
          v-model="sie"
          type="number"
          class="w-100"
          :class="{ invalid: error }"
          :label="searchLabel"
          :placeholder="searchPlaceholder"
          icon="fas fa-search text-orange"
          left-icon
        />
      </div>
      <div class="d-flex align-items-end justify-content-end">
        <action-button
          id="btnSave"
          :i="loading ? 'fas fa-cog fa-spin' : 'fas fa-file-plus'"
          text="Añadir"
          :variant="canAdd && !loading ? 'success' : 'secondary'"
          :disabled="loading || !canAdd"
          @click="add"
        />
      </div>
    </div>
    <custom-error
      v-if="error || statusError"
      variant="no-border"
      :message="statusError || customErrorMsg"
    />

    <div v-if="hasSelected && showSelected" class="my-3">
      <div class="my-3">
        <icon-view
          icon="fas fa-user-circle fa-lg text-orange"
          variant="custom-icon-view"
          icon-background="white"
          :title="title"
        />
      </div>
      <div
        class="px-3 my-3"
        v-for="(item, index) in model"
        :key="item.studentId"
      >
        <div class="row font-weight-normal p-3 rounded custom-row">
          <div class="col-md-3 col-12">
            <div class="color-secondary-6 font-14">
              Nombre del Estudiante:
            </div>
            <div class="color-black font-16 text-break">
              {{ item.fullName | empty }}
            </div>
          </div>
          <div class="col-md col-12">
            <div class="color-secondary-6 font-14">
              SIE:
            </div>
            <div class="color-black font-16 text-break">
              {{ item.studentId | empty }}
            </div>
          </div>
          <div class="col-md col-12">
            <div class="color-secondary-6 font-14">
              Edad:
            </div>
            <div class="color-black font-16 text-break">
              {{ item.age | age | empty }}
            </div>
          </div>
          <div class="col-md-3 col-12">
            <div class="color-secondary-6 font-14">
              Impedimento:
            </div>
            <div class="color-black font-16 text-break">
              {{ item.impediment | empty }}
            </div>
          </div>
          <div class="col-md col-12">
            <div class="color-secondary-6 font-14">
              Estatus:
            </div>
            <div class="color-black font-16 text-break">
              {{ item.status | empty }}
            </div>
          </div>
          <div
            class="col-md-1 col-12 d-flex align-items-center justify-content-end"
          >
            <action-button
              v-if="!disabled"
              i="fas fa-times-circle"
              variant="outline-danger"
              @click="quit(index)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IconView from 'icon-view';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import studentService from '@/services/student.js';
import resourcesService from '@/services/resources.js';
import alertMixin from '@/mixin/alert.mixin';
import CustomError from 'custom-error';
import StudentStatuses from '@/utils/constants/student/studentStatuses.js';

export default {
  name: 'StudentSieSearch',
  mixins: [alertMixin],
  components: {
    IconView,
    CustomInput,
    ActionButton,
    CustomError,
  },
  props: {
    value: {
      type: Array,
    },
    limit: {
      type: Number,
      default: () => null,
    },
    statusesAllowed: {
      type: Array,
      default: () => [],
    },
    customErrorMsg: {
      type: String,
      default: () => null,
    },
    error: {
      type: String,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchLabel: {
      type: String,
      default: () => 'SIE del Estudiante:',
    },
    searchPlaceholder: {
      type: String,
      default: () => 'Escriba Número de SIE',
    },
    showSelected: {
      type: Boolean,
      default: true,
    },
    studentSearchHandler: {
      type: Function,
      default: null,
    },
  },
  filters: {
    age(val) {
      if (!val) return null;
      return `${val}`.split('.')[0];
    },
  },
  data() {
    return {
      sie: null,
      loading: false,
      resources: [],
      statusError: null,
    };
  },
  async mounted() {
    await this.getResources();
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    age() {
      return () => `${student.age}`.split('.')[0];
    },
    title() {
      return this.model.length === 1
        ? 'Estudiante Seleccionado'
        : 'Estudiante(s) Seleccionado(s)';
    },
    hasSelected() {
      return this.model.length > 0;
    },
    limitReached() {
      return this.model.length === this.limit;
    },
    canAdd() {
      const valid =
        this.sie?.length === 8 && Number.isInteger(Number(this.sie));

      return valid;
    },
    allowedStatusesText() {
      const statuses = Object.entries(StudentStatuses)
        .filter(([_, value]) => this.statusesAllowed.includes(value))
        .map(([key]) => key);

      if (!statuses.length) {
        return 'un estatus válido';
      }

      if (statuses.length === 1) {
        return statuses[0];
      }

      return `${statuses.slice(0, -1).join(', ')} o ${
        statuses[statuses.length - 1]
      }`;
    },
  },
  methods: {
    async getResources() {
      if (this.resources.length) return;
      this.resources = await resourcesService.LoadValidationMessages(['44']);
    },
    async add() {
      if (!this.canAdd) return;

      if (this.model.some((x) => x.studentId == this.sie)) {
        this.ShowToast('Error', 'El estudiante ya ha sido agregado.', 'error');
        return;
      }
      if (this.limitReached) {
        this.ShowToast(
          'Error',
          `Solo esta permitido agregar ${this.limit} estudiante(s).`,
          'error'
        );
        return;
      }
      this.statusError = null;
      this.loading = true;
      try {
        const data = await this.fetchStudent();
        if (!data.canAccess) throw new Error(this.resources['44']);

        const validStatus =
          !this.statusesAllowed.length ||
          this.statusesAllowed.includes(data.statusId);
        if (!validStatus) {
          this.statusError = `El estatus del estudiante debe ser ${this.allowedStatusesText}.`;
          throw new Error('El estudiante no esta en un estatus permitido.');
        }

        this.model.push(data);
        this.sie = null;
        this.loading = false;
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ?? error?.message,
          'error'
        );
        this.loading = false;
      }
    },
    async fetchStudent() {
      if (!this.studentSearchHandler) {
        const { data } = await studentService.getBySie(this.sie);
        return data;
      }

      const response = await this.studentSearchHandler(this.sie);
      const students = response?.data ?? [];
      const student = Array.isArray(students) ? students[0] : students;

      if (!student) {
        throw new Error('No se encontraron estudiantes.');
      }

      return {
        ...student,
        studentId: student.studentId ?? student.studentSieId,
        fullName: student.fullName ?? student.name,
        canAccess: student.canAccess ?? true,
      };
    },
    quit(index) {
      this.model.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.custom-row {
  background-color: #f7f7f7;
  border-left: 4px solid #b5c3bc;
}

.invalid ::v-deep input {
  background-color: #f7d7da;
  border: 1px solid #ff0018 !important;
}
</style>
